import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize, X } from 'lucide-react';
import Hls from 'hls.js';

// Video Player Modal Component with HLS support
export const VideoPlayerModal = ({ isOpen, onClose, videoUrl, title }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const containerRef = useRef(null);
  const controlsTimeout = useRef(null);

  // Check if URL is HLS stream
  const isHLS = videoUrl && (videoUrl.includes('.m3u8') || videoUrl.includes('b-cdn.net'));

  // Convert video URL to streaming endpoint for local videos
  const getVideoUrl = (url) => {
    if (!url) return url;
    if (url.includes('/api/uploads/videos/')) {
      const filename = url.split('/').pop();
      return `/api/stream/video/${filename}`;
    }
    return url;
  };

  const finalVideoUrl = getVideoUrl(videoUrl);

  // Setup HLS or native video
  useEffect(() => {
    if (!isOpen || !videoRef.current || !finalVideoUrl) return;

    const video = videoRef.current;

    if (isHLS && Hls.isSupported()) {
      // Use HLS.js for .m3u8 streams
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });
      hlsRef.current = hls;
      hls.loadSource(finalVideoUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
        setIsPlaying(true);
      });
    } else if (isHLS && video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      video.src = finalVideoUrl;
      video.play().catch(() => {});
      setIsPlaying(true);
    } else {
      // Regular video
      video.src = finalVideoUrl;
      video.play().catch(() => {});
      setIsPlaying(true);
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [isOpen, finalVideoUrl, isHLS]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === ' ') {
        e.preventDefault();
        togglePlay();
      }
      if (e.key === 'm') toggleMute();
      if (e.key === 'f') toggleFullscreen();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const dur = videoRef.current.duration;
      setCurrentTime(current);
      setProgress((current / dur) * 100);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleProgressClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = pos * videoRef.current.duration;
    }
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeout.current) {
      clearTimeout(controlsTimeout.current);
    }
    controlsTimeout.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      <div 
        ref={containerRef}
        className="relative w-full max-w-6xl mx-4"
        onClick={(e) => e.stopPropagation()}
        onMouseMove={handleMouseMove}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className={`absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-opacity z-10 ${showControls ? 'opacity-100' : 'opacity-0'}`}
        >
          <X className="w-8 h-8" />
        </button>

        {/* Video Title */}
        <div className={`absolute -top-12 left-0 text-white font-medium text-lg transition-opacity ${showControls ? 'opacity-100' : 'opacity-0'}`}>
          {title}
        </div>

        {/* Video Container */}
        <div className="relative aspect-video bg-black rounded-xl overflow-hidden shadow-2xl">
          <video
            ref={videoRef}
            className="w-full h-full object-contain"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={() => setIsPlaying(false)}
            onClick={togglePlay}
            playsInline
          />

          {/* Play/Pause Overlay */}
          {!isPlaying && (
            <div 
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
              onClick={togglePlay}
            >
              <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors">
                <Play className="w-10 h-10 text-white ml-1" />
              </div>
            </div>
          )}

          {/* Controls */}
          <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity ${showControls ? 'opacity-100' : 'opacity-0'}`}>
            {/* Progress Bar */}
            <div 
              className="w-full h-1.5 bg-white/20 rounded-full cursor-pointer mb-4 group"
              onClick={handleProgressClick}
            >
              <div 
                className="h-full bg-[#2e8036] rounded-full relative"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" />
              </div>
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={togglePlay}
                  className="p-2 text-white hover:text-[#2e8036] transition-colors"
                >
                  {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                </button>
                
                <button
                  onClick={toggleMute}
                  className="p-2 text-white hover:text-[#2e8036] transition-colors"
                >
                  {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                </button>

                <span className="text-white/80 text-sm font-mono">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>

              <button
                onClick={toggleFullscreen}
                className="p-2 text-white hover:text-[#2e8036] transition-colors"
              >
                {isFullscreen ? <Minimize className="w-6 h-6" /> : <Maximize className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Video Card Component for Landing Page
export const VideoCard = ({ video, onClick, accentColor = '#3a7189', primaryColor = '#93a5af' }) => {
  const [isHovered, setIsHovered] = useState(false);
  const videoPreviewRef = useRef(null);
  const hlsRef = useRef(null);
  
  const isLocalVideo = video.videoUrl && !video.videoUrl.includes('youtube') && !video.videoUrl.includes('vimeo');
  const isHLS = video.videoUrl && (video.videoUrl.includes('.m3u8') || video.videoUrl.includes('b-cdn.net'));

  // Convert video URL to streaming endpoint for local videos
  const getVideoUrl = (url) => {
    if (!url) return url;
    if (url.includes('/api/uploads/videos/')) {
      const filename = url.split('/').pop();
      return `/api/stream/video/${filename}`;
    }
    return url;
  };

  const finalVideoUrl = getVideoUrl(video.videoUrl);

  // Setup HLS on mount for preview
  useEffect(() => {
    if (!isHLS || !videoPreviewRef.current) return;
    
    const videoEl = videoPreviewRef.current;
    
    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });
      hlsRef.current = hls;
      hls.loadSource(finalVideoUrl);
      hls.attachMedia(videoEl);
    } else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      videoEl.src = finalVideoUrl;
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [isHLS, finalVideoUrl]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    if ((isLocalVideo || isHLS) && videoPreviewRef.current) {
      videoPreviewRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if ((isLocalVideo || isHLS) && videoPreviewRef.current) {
      videoPreviewRef.current.pause();
      videoPreviewRef.current.currentTime = 0;
    }
  };

  return (
    <div 
      className="bg-[#0d1218] border border-white/5 rounded-3xl overflow-hidden hover:border-[#3a7189]/30 hover:shadow-xl hover:shadow-[#3a7189]/5 transition-all group cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
    >
      <div className="aspect-video relative bg-[#0d1218] overflow-hidden">
        {(isLocalVideo || isHLS) ? (
          <>
            {/* Thumbnail or Video */}
            {video.thumbnail ? (
              <>
                <img 
                  src={video.thumbnail} 
                  alt={video.title}
                  className={`w-full h-full object-cover transition-opacity duration-300 ${isHovered ? 'opacity-0' : 'opacity-100'}`}
                />
                <video
                  ref={videoPreviewRef}
                  src={!isHLS ? finalVideoUrl : undefined}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                  muted
                  loop
                  playsInline
                  preload="none"
                />
              </>
            ) : (
              <video
                ref={videoPreviewRef}
                src={!isHLS ? finalVideoUrl : undefined}
                className="w-full h-full object-cover"
                muted
                loop
                playsInline
                preload="metadata"
              />
            )}
            
            {/* Play Button Overlay - hidden when hovering */}
            <div className={`absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity duration-300 ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center backdrop-blur-sm transition-all group-hover:scale-110"
                style={{ backgroundColor: `${accentColor}40` }}
              >
                <Play className="w-8 h-8 text-white ml-1" />
              </div>
            </div>
            
            {/* Hover indicator */}
            <div className={`absolute bottom-4 left-4 right-4 flex items-center justify-center transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
              <span className="px-4 py-2 bg-black/60 backdrop-blur-sm rounded-full text-white text-sm font-medium">
                Clique para ampliar
              </span>
            </div>
          </>
        ) : video.embedUrl ? (
          // YouTube/Vimeo embed - lazy loaded
          <div className="relative w-full h-full">
            <iframe
              src={video.embedUrl}
              title={video.title}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
        ) : (
          // Placeholder
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#3a7189]/20 to-[#1c262d]/50">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center backdrop-blur group-hover:scale-110 transition-all"
              style={{ backgroundColor: `${accentColor}30` }}
            >
              <Play className="w-8 h-8 text-white ml-1" />
            </div>
          </div>
        )}

        {/* Duration Badge (if available) */}
        {video.duration && (
          <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/70 backdrop-blur-sm rounded text-white text-xs font-mono">
            {video.duration}
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#3a7189] transition-colors">
          {video.title}
        </h3>
        <p style={{ color: primaryColor }}>{video.description}</p>
      </div>
    </div>
  );
};

export default VideoPlayerModal;
